import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Linkedin, Sparkles } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { useI18n } from "@/i18n/I18nContext";

const WHATSAPP_URL = "https://wa.me/33672328408";
const LINKEDIN_URL = "https://linkedin.com/in/diallobirrou";

export default function Hero() {
  const { t } = useI18n();
  const h = t.hero;

  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative pt-32 lg:pt-40 pb-20 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="absolute -top-40 -right-40 h-[640px] w-[640px] rounded-full bg-cyan-100/40 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-amber-100/30 blur-3xl" />
      </div>

      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs uppercase tracking-[0.22em] text-slate-700">
            <Sparkles className="h-3.5 w-3.5 text-gold" />
            {h.badge}
          </span>

          <h1
            data-testid={HERO.title}
            className="mt-8 font-display font-semibold tracking-tight text-navy text-4xl sm:text-5xl lg:text-[64px] leading-[1.05]"
          >
            {h.titleA}
            <span className="relative inline-block">
              <span className="relative z-10">{h.titleHighlight}</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-cyan-tech/60 -z-0 rounded-sm" />
            </span>
            {h.titleEnd}
          </h1>

          <p
            data-testid={HERO.subtitle}
            className="mt-6 text-base sm:text-lg text-slate-700 max-w-2xl leading-relaxed"
          >
            {h.subtitle}
          </p>

          <p className="mt-4 text-sm text-slate-500 max-w-2xl">{h.micro}</p>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={HERO.ctaWhatsapp}
              className="btn-primary"
            >
              <MessageCircle className="h-4 w-4" /> {h.ctaWhatsapp}
            </a>
            <a
              href="#expertises"
              data-testid={HERO.ctaExpertises}
              className="btn-outline"
            >
              {h.ctaExpertises} <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={HERO.ctaLinkedin}
              className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium text-navy hover:text-cyan-700 transition-colors"
            >
              <Linkedin className="h-4 w-4" /> {h.ctaLinkedin}
            </a>
          </div>
        </motion.div>

        <motion.div
          data-testid={HERO.proofStrip}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 lg:mt-24 grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-200 rounded-2xl border border-slate-200 bg-white/80 backdrop-blur"
        >
          {h.proofs.map((p, i) => (
            <div key={i} className="px-5 py-6 text-center first:pl-6 last:pr-6">
              {p.value && (
                <div className="font-display text-2xl lg:text-3xl font-semibold text-navy">
                  {p.value}
                </div>
              )}
              <div className={`text-[11px] uppercase tracking-[0.18em] mt-1 ${p.value ? "text-slate-500" : "text-navy font-medium"}`}>
                {p.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
